import * as PIXI from 'pixi.js';
import isEqual from 'lodash/isEqual';
import { getAttributesToSerialize, WatermarkAttribute } from '../attributes';
import { WatermarkPositions } from '../../enums';
import { TimeInvariantBase } from '../TimeInvariantBase';
import { Config } from '../../video-data/config';
import type { MediaSourceData } from '../asset/MediaAsset';
import type { WatermarkThemeRef } from '../attributes/template/common';
import { assertInArray } from '../../validators';
import { getReferenceResolution } from '../../libs/videoData';
import type { Scene } from '../scene/Scene';
import { TimeAwareMediaAsset } from '../asset/TimeAwareMediaAsset';
import { VideoData } from '../../video-data';
import type { AspectRatio } from '../../aspectRatioConfig';
import type { WatermarkLayout } from './WatermarkLayout';
import type { BaseSettings } from '../TimeAwareBase';
import { comparer, observable, reaction, runInAction } from '../reactivity';

export type SceneWatermarkManagerSettings = BaseSettings & {
    getAspectRatio: () => AspectRatio;
};

type SourceDataOrInherit =
    | MediaSourceData
    | typeof Config.INHERIT_FROM_TEMPLATE
    | typeof Config.WATERMARK_NONE
    | WatermarkThemeRef;
export type SceneWatermarkPosition = WatermarkPositions | typeof Config.INHERIT_FROM_TEMPLATE;

type WatermarkLayoutValues = {
    marginLeft: number;
    marginTop: number;
    marginRight: number;
    marginBottom: number;
    maxWidth: number;
    maxHeight: number;
};

type WatermarkState = {
    sourceData: SourceDataOrInherit;
    visible: boolean;
    fillMode: TimeAwareMediaAsset['fillMode'];
    opacity: number;
    aspectRatio: AspectRatio;
    layout: WatermarkLayoutValues;
    templatePosition: SceneWatermarkPosition;
    templatePositions: readonly WatermarkPositions[];
    watermarkAssetId: string | null;
} | null;

const normalizeWatermarkSourceData = (sourceData: MediaSourceData): object => {
    const normalized: Partial<MediaSourceData> & Record<string, unknown> = {
        ...sourceData,
    };

    delete normalized.attribution;
    delete normalized.transform;
    delete normalized.type;
    delete normalized.uuid;
    delete normalized.title;

    return normalized;
};

const isSourceDataEqual = (sourceDataA: MediaSourceData, sourceDataB: MediaSourceData): boolean => {
    return isEqual(
        normalizeWatermarkSourceData(sourceDataA),
        normalizeWatermarkSourceData(sourceDataB),
    );
};

/**
 * This class manages the watermark on the scene level and overrides any template-level settings.
 * The template-level settings are found in luminary.template.watermark, which is a TemplateWatermarkSettings.
 */
export class SceneWatermarkManager extends TimeInvariantBase {
    override _classType: string = 'SceneWatermarkManager';

    @(WatermarkAttribute({ supportsInherit: true }) as <This>(
        target: ClassAccessorDecoratorTarget<This, SourceDataOrInherit>,
        context: ClassAccessorDecoratorContext<This, SourceDataOrInherit>,
    ) => ClassAccessorDecoratorResult<This, SourceDataOrInherit>)
    @observable
    accessor sourceData: SourceDataOrInherit = Config.INHERIT_FROM_TEMPLATE;
    @observable accessor #position: SceneWatermarkPosition = Config.INHERIT_FROM_TEMPLATE;
    #positionBeforeBump: SceneWatermarkPosition | null = null;
    #getAspectRatio: () => AspectRatio;

    constructor(settings: SceneWatermarkManagerSettings) {
        super(settings);
        this.#getAspectRatio = settings.getAspectRatio;

        this.addReaction(
            reaction(
                () => SceneWatermarkManager.#watermarkState(this),
                (state) => SceneWatermarkManager.#syncWatermark(this, state),
                {
                    equals: comparer.structural,
                    name: 'SceneWatermarkManager.watermark',
                    fireImmediately: false,
                },
            ),
        );
    }

    static #watermarkState(manager: SceneWatermarkManager): WatermarkState {
        const scene = manager.parent as Scene | null;
        if (manager.destroyed || !scene || scene.destroyed) {
            return null;
        }
        return {
            sourceData: manager.sourceData,
            visible: manager.template.watermark.visible,
            fillMode: manager.template.watermark.fillMode,
            opacity: manager.opacity,
            aspectRatio: scene._aspectRatio,
            layout: SceneWatermarkManager.#layoutValues(
                manager.template.watermark.getLayout(scene._aspectRatio),
            ),
            templatePosition: manager.template.watermark.position,
            templatePositions: manager.template.watermark.availablePositions,
            watermarkAssetId: scene.getAssetByName('watermark_1', { deep: false })?.id ?? null,
        };
    }

    static #layoutValues(layout: WatermarkLayout): WatermarkLayoutValues {
        return {
            marginLeft: layout.marginLeft,
            marginTop: layout.marginTop,
            marginRight: layout.marginRight,
            marginBottom: layout.marginBottom,
            maxWidth: layout.maxWidth,
            maxHeight: layout.maxHeight,
        };
    }

    #currentLayoutValues(): WatermarkLayoutValues {
        return SceneWatermarkManager.#layoutValues(
            this.template.watermark.getLayout(this.#getAspectRatio()),
        );
    }

    static #syncWatermark(manager: SceneWatermarkManager, state: WatermarkState) {
        if (state === null) {
            return;
        }
        manager.#applyWatermarkState(state);
    }

    get #templateWatermarkPosition() {
        return this.template.watermark.position;
    }

    /**
     * The position (corner) of the watermark
     */
    get position(): SceneWatermarkPosition {
        if (this.#position === Config.INHERIT_FROM_TEMPLATE) {
            return this.#templateWatermarkPosition;
        }
        return this.#position;
    }

    get rawPosition(): SceneWatermarkPosition {
        return this.#positionBeforeBump ?? this.#position;
    }

    set position(position: SceneWatermarkPosition) {
        if (this.#position === position) {
            return;
        }
        const validPositions = [...this.availablePositions, Config.INHERIT_FROM_TEMPLATE];
        assertInArray(position, validPositions);
        // Reset position before bump so that we don't jump to the previous position or save the previous posit
        runInAction(() => {
            this.#positionBeforeBump = null;
            this.#position = position;
        });
        // TODO(LUM-595): the override syncs here rather than waking the derivation, because `#position` is
        // deliberately absent from the tracked state -- the bump writes it from inside the effect, so tracking
        // it would re-enter. This is a different cause from the bounds problem in `Scene`'s childUpdated handler:
        // retiring this one needs the bump to stop storing its result, which only becomes possible once
        // placement derives post-draw.
        this._syncWatermark();
        this.requiresUpdate = true;
    }

    /**
     * List of allowed watermark position for this scene
     */
    get availablePositions(): readonly SceneWatermarkPosition[] {
        return this.#availablePositionsFor(
            this.#currentLayoutValues(),
            this.#getAspectRatio(),
            this.template.watermark.availablePositions,
        );
    }

    #availablePositionsFor(
        layout: WatermarkLayoutValues,
        aspectRatio: AspectRatio,
        templatePositions: readonly WatermarkPositions[],
    ): SceneWatermarkPosition[] {
        const textAssets = (this.parent as Scene)
            .getAssetsByType(Config.TEXT_ASSET_TYPE)
            .filter((asset) => asset.isEditable);
        let currentBounds: PIXI.Rectangle;
        const watermarkAsset = this.#getMediaAsset();
        if (watermarkAsset) {
            currentBounds = watermarkAsset._pixiContainer.getBounds();
        }
        return templatePositions.filter((position) => {
            const bounds = this.#getBoundsForPosition(position, layout, aspectRatio, currentBounds);
            return !textAssets.some((textAsset) =>
                bounds.intersects(textAsset._pixiContainer.getBounds()),
            );
        });
    }

    /**
     * Since the position getter may return a value from the template, we need to explicitly
     * serialize the underlying value.
     */
    _positionCustomSave() {
        return this.rawPosition;
    }

    get opacity() {
        return this.template.watermark.opacity;
    }

    #getBoundsForPosition(
        position: WatermarkPositions,
        layout: WatermarkLayoutValues,
        aspectRatio: AspectRatio,
        currentBounds?: PIXI.Rectangle,
    ): PIXI.Rectangle {
        let { maxWidth, maxHeight, transformation } = this.#computeLayoutForPosition(
            position,
            layout,
            aspectRatio,
        );
        if (currentBounds) {
            // If the current bounds of the watermark are known, then use those
            maxWidth = currentBounds.width;
            maxHeight = currentBounds.height;
        }
        const x = transformation.position.x - transformation.anchor.x * maxWidth;
        const y = transformation.position.y - transformation.anchor.y * maxHeight;
        return new PIXI.Rectangle(x, y, maxWidth, maxHeight);
    }

    /**
     * Find the position & size of the watermark for any position, based on Aspect Ratio.
     */
    #computeLayoutForPosition(
        position: SceneWatermarkPosition,
        activeLayout: WatermarkLayoutValues,
        aspectRatio: AspectRatio,
    ) {
        const resolution = getReferenceResolution(aspectRatio);

        let transformation = null;

        switch (position) {
            case WatermarkPositions.TopLeft:
                transformation = {
                    position: {
                        x: activeLayout.marginLeft,
                        y: activeLayout.marginTop,
                    },
                    anchor: {
                        x: 0,
                        y: 0,
                    },
                };
                break;
            case WatermarkPositions.BottomLeft:
                transformation = {
                    position: {
                        x: activeLayout.marginLeft,
                        y: resolution.height - activeLayout.marginBottom,
                    },
                    anchor: {
                        x: 0,
                        y: 1,
                    },
                };
                break;
            case WatermarkPositions.TopRight:
                transformation = {
                    position: {
                        x: resolution.width - activeLayout.marginRight,
                        y: activeLayout.marginTop,
                    },
                    anchor: {
                        x: 1,
                        y: 0,
                    },
                };
                break;
            case WatermarkPositions.BottomRight:
                transformation = {
                    position: {
                        x: resolution.width - activeLayout.marginRight,
                        y: resolution.height - activeLayout.marginBottom,
                    },
                    anchor: {
                        x: 1,
                        y: 1,
                    },
                };
                break;
            case WatermarkPositions.BottomCenter:
                transformation = {
                    position: {
                        x: resolution.width / 2,
                        y: resolution.height - activeLayout.marginBottom,
                    },
                    anchor: {
                        x: 0.5,
                        y: 1,
                    },
                };
                break;
            case WatermarkPositions.Center:
                transformation = {
                    position: {
                        x: resolution.width / 2,
                        y: resolution.height / 2,
                    },
                    anchor: {
                        x: 0.5,
                        y: 0.5,
                    },
                };
                break;
            case WatermarkPositions.TopCenter:
                transformation = {
                    position: {
                        x: resolution.width / 2,
                        y: activeLayout.marginTop,
                    },
                    anchor: {
                        x: 0.5,
                        y: 0,
                    },
                };
                break;
            case WatermarkPositions.CenterLeft:
                transformation = {
                    position: {
                        x: activeLayout.marginLeft,
                        y: resolution.height / 2,
                    },
                    anchor: {
                        x: 0,
                        y: 0.5,
                    },
                };
                break;
            case WatermarkPositions.CenterRight:
                transformation = {
                    position: {
                        x: resolution.width - activeLayout.marginRight,
                        y: resolution.height / 2,
                    },
                    anchor: {
                        x: 1,
                        y: 0.5,
                    },
                };
                break;
            default:
                throw new Error(`Position is invalid: ${position}`);
        }

        return {
            maxWidth: activeLayout.maxWidth,
            maxHeight: activeLayout.maxHeight,
            transformation,
        };
    }

    removeWatermarkIfExists() {
        const scene = this.parent as Scene;
        if (scene.hasAssetByName('watermark_1')) {
            const watermark = scene.getAssetByName('watermark_1')!;
            watermark.destroy(true);
        }
    }

    #hideWatermarkIfExists() {
        const scene = this.parent as Scene;
        if (scene.hasAssetByName('watermark_1')) {
            const watermark = scene.getAssetByName('watermark_1')!;
            if (watermark.sourceUrl === VideoData.INVISIBLE_IMAGE) {
                return;
            }

            watermark.sourceData = VideoData.INVISIBLE_IMAGE_SOURCE_DATA as MediaSourceData;
        }
    }

    /** @internal */
    _syncWatermark() {
        const state = SceneWatermarkManager.#watermarkState(this);
        if (state === null) {
            return;
        }
        this.#applyWatermarkState(state);
    }

    #applyWatermarkState({
        sourceData,
        visible,
        fillMode,
        opacity,
        layout,
        aspectRatio,
        templatePosition,
        templatePositions,
    }: NonNullable<WatermarkState>) {
        if (this.destroyed) {
            return;
        }
        this.slide!.batchRun(() => {
            if (sourceData === Config.WATERMARK_NONE || !visible) {
                // No watermark or can't bump to any position
                this.#hideWatermarkIfExists();
                return;
            }

            const watermark = this.#getOrCreateMediaAsset();
            SceneWatermarkManager.#applySettings(watermark, sourceData, fillMode, opacity);
            this.#applyPlacement(layout, aspectRatio, templatePosition, templatePositions);
        });
    }

    #applyPlacement(
        layout: WatermarkLayoutValues,
        aspectRatio: AspectRatio,
        templatePosition: SceneWatermarkPosition,
        templatePositions: readonly WatermarkPositions[],
    ) {
        if (this.destroyed) {
            return;
        }

        const watermark = this.#getMediaAsset();
        if (!watermark) {
            return;
        }

        const availablePositions = this.#availablePositionsFor(
            layout,
            aspectRatio,
            templatePositions,
        );

        this.#updatePosition(availablePositions, templatePosition);
        this.#updateMediaAssetTransform(watermark, layout, aspectRatio);
        // Update visibility after the source data and transform have been updated.
        // We toggle the watermark's visibility to preserve its boundaries for accurate overlap calculations,
        // as the availablePositions rely on these bounds. Removing the watermark when no positions are available
        // would cause a loss of the exact boundary data. This approach of hiding, rather than removing the
        // watermark, avoids disruptions in overlap calculations and potential infinite loops.
        watermark.visibleWithUpdate = availablePositions.length !== 0;
        SceneWatermarkManager.#moveWatermarkToTop(this.slide, watermark);
    }

    static #moveWatermarkToTop(scene: Scene | null, watermark: TimeAwareMediaAsset) {
        if (!scene || !scene.scene) {
            return;
        }
        const children = scene.scene._children;
        const watermarkIndex = children.indexOf(watermark);
        const lastIndex = children.length - 1;

        if (watermarkIndex !== -1 && watermarkIndex !== lastIndex) {
            scene.scene.moveChild(watermark, lastIndex);
        }
    }

    #getOrCreateMediaAsset(): TimeAwareMediaAsset {
        const scene = this.slide!;

        let watermark = this.#getMediaAsset();
        if (!watermark) {
            watermark = new TimeAwareMediaAsset(scene.scene, {
                name: 'watermark_1',
                updateEventSource: this,
                updateParentOnRedraw: true,
                autoDraw: (scene as Scene).autoDraw,
                timeline: scene.scene.root.timeline,
                fetchResources: this.fetchResources,
                audioMode: scene.scene.root.audioMode,
                assetNetworkManager: this.root._assetNetworkManager,
                getAspectRatio: () => this.#getAspectRatio(),
                seekWaitTime: scene.scene.root.settings.video?.seekWaitTime ?? 2000,
                reusableTextures: scene.scene._reusableTextures,
                mediaSizeToUse: this.root.mediaSizeToUse,
                getReferenceResolution: () => scene.scene.root.referenceResolution,
                getOutputResolution: () => scene.scene.root.outputResolution,
            });
            watermark.kenburn = 'none';
            watermark.animationConfig = [];
            watermark.isEditable = false;
            watermark.mask = null;
            watermark.generateNewId();
            scene.scene.insertAsset(watermark);
        }

        return watermark;
    }

    #getMediaAsset(): TimeAwareMediaAsset | undefined {
        const scene = this.slide!;
        return scene.getAssetByName('watermark_1', { deep: false });
    }

    static #applySettings(
        watermark: TimeAwareMediaAsset,
        sourceData: SourceDataOrInherit,
        fillMode: TimeAwareMediaAsset['fillMode'],
        opacity: number,
    ) {
        if (!isSourceDataEqual(watermark.sourceData, sourceData as MediaSourceData)) {
            watermark.sourceData = {
                ...(sourceData as Object),
                type: Config.IMAGE_TYPE_BACKGROUND,
            } as MediaSourceData;
        }

        watermark.fillMode = fillMode;
        watermark.opacity = opacity;
    }

    #updateMediaAssetTransform(
        watermark: TimeAwareMediaAsset,
        layout: WatermarkLayoutValues,
        aspectRatio: AspectRatio,
    ) {
        const { maxWidth, maxHeight, transformation } = this.#computeLayoutForPosition(
            this.position,
            layout,
            aspectRatio,
        );

        watermark.transformation.position.load(transformation.position);
        watermark.transformation.anchor.load(transformation.anchor);
        watermark.maxWidth = maxWidth;
        watermark.maxHeight = maxHeight;
        watermark.applyTransformation();
    }

    #updatePosition(
        availablePositions: SceneWatermarkPosition[],
        templatePosition: SceneWatermarkPosition,
    ) {
        /**
         * "Bump" to next available position if we can't display the watermark in the current position.
         * This can happen when a text asset begins overlapping a watermark's bounds.
         */
        if (availablePositions.length === 0) {
            // Watermark cannot be displayed, position is irrelevant.
            return;
        }

        if (this.#positionBeforeBump !== null) {
            const positionBeforeBump =
                this.#positionBeforeBump === Config.INHERIT_FROM_TEMPLATE
                    ? templatePosition
                    : this.#positionBeforeBump;

            if (availablePositions.includes(positionBeforeBump)) {
                runInAction(() => {
                    this.#position = this.#positionBeforeBump!;
                    this.#positionBeforeBump = null;
                });
                return;
            }
        }

        const currentPosition =
            this.#position === Config.INHERIT_FROM_TEMPLATE ? templatePosition : this.#position;
        if (!availablePositions.includes(currentPosition)) {
            runInAction(() => {
                if (this.#positionBeforeBump === null) {
                    this.#positionBeforeBump = this.#position;
                }
                this.#position = availablePositions[0];
            });
        }
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize
            .concat(['position'])
            .concat(getAttributesToSerialize(this));
    }
}
